import { Component, ErrorHandler, OnInit } from '@angular/core';

import { BsModalRef } from 'ngx-bootstrap/modal';
import { IWeissmanModalComponent } from '../Compliance/WeissmanModalService';
import { TaskService } from './task.service.upgrade';
import { ModalWrapperComponent } from '../UI-Lib/Modal-Wrapper/modalWrapper.component';
import { TaskSeriesArrowsComponent } from './Task-Series-Arrows/taskSeriesArrows.component';
import { TaskSeriesComponent } from './Task-Series/taskSeries.component';
import { MessageModalService } from '../UI-Lib/Message-Box/messageModal.service';

import * as _ from 'lodash';
import moment from 'moment';

export interface TaskModalParams {
    tasks: any[];
    // Passed only by screens that can show the Add Year dialog. Completing a task then asks the server to return an
    // Add Year prompt instead of silently creating the subsequent Tax Year, and the modal closes after reporting it.
    onAddYearPrompt?: (prompt: Core.AddYearPromptInfo) => void;
}

@Component({
    selector: 'task-modal',
    templateUrl: './taskModal.component.html',
    imports: [ModalWrapperComponent, TaskSeriesArrowsComponent, TaskSeriesComponent],
})
export class TaskModalComponent implements OnInit, IWeissmanModalComponent<TaskModalParams, boolean> {
    constructor(
        private readonly _bsModalRef: BsModalRef,
        private readonly _taskService: TaskService,
        private readonly _messageModalService: MessageModalService,
        private readonly _errorHandler: ErrorHandler,
    ) {}

    params: TaskModalParams;
    result: boolean;

    heights = {
        sm: 50,
        md: 75,
        lg: 100,
        xl: 125,
    };

    chevronWidth = 244;
    chevronPadding = 10;
    taskSeriesPadding = 10;
    waitingOnServer = false;
    taskGroupsBySequence = [];

    private pendingTaskOperations = 0;
    private taskOperationMessageOpen = false;
    private closeWhenIdle = false;

    ngOnInit(): void {
        this.taskGroupsBySequence = this.params.tasks.map((x) => this.getTaskGroupHeights(x));
    }

    getTaskGroupHeights(taskGroupCollection) {
        taskGroupCollection.taskGroups = _.chain(taskGroupCollection.taskGroups)
            .sortBy([
                (taskGroup) => {
                    const maxDueDate = _.chain(taskGroup.tasks)
                        .map((task: any) => {
                            return task.dueDate ? moment(task.dueDate).startOf('day').toDate().getTime() : null;
                        })
                        .compact()
                        .max()
                        .value();

                    return maxDueDate === -Infinity ? null : maxDueDate;
                },
                (taskGroup) => {
                    return taskGroup.tasks[0].entityId;
                },
            ])
            .map((taskGroup) => {
                taskGroup.height = _.reduce(
                    taskGroup.tasks,
                    (result, task) => {
                        return result + this.getHeight(task);
                    },
                    0,
                );
                taskGroup.height += this.chevronPadding;
                taskGroup.height += (_.last(taskGroup.tasks) as any).pointsTo.length
                    ? this.chevronPadding
                    : this.heights.lg;

                return taskGroup;
            })
            .value();

        const maxHeight = (_.maxBy(taskGroupCollection.taskGroups, 'height') as any).height;

        taskGroupCollection.taskGroups = _.map(taskGroupCollection.taskGroups, (taskGroup: any) => {
            taskGroup.maxHeight = maxHeight;
            return taskGroup;
        });

        return taskGroupCollection;
    }

    updateData(data: any[]): void {
        this.taskGroupsBySequence = _.map(data, this.getTaskGroupHeights.bind(this));
    }

    taskUpdated(): void {
        this.result = true;
    }

    taskOperationStarted(operation: Promise<void>): void {
        this.pendingTaskOperations++;
        void operation.then(
            () => {
                this.pendingTaskOperations--;
                this.closeIfRequested();
            },
            (error: unknown) => {
                this.pendingTaskOperations--;
                this.closeIfRequested();
                throw error;
            },
        );
    }

    // Reports the prompt to the screen that opened the modal and closes once the task operation that returned it has
    // finished, so the Add Year dialog is the only modal open.
    addYearPromptRequested(prompt: Core.AddYearPromptInfo): void {
        this.params.onAddYearPrompt?.(prompt);
        this.closeWhenIdle = true;
    }

    canDismiss = async (): Promise<boolean> => {
        if (this.taskOperationMessageOpen) {
            return Promise.reject();
        }

        if (this.pendingTaskOperations === 0) {
            return true;
        }

        this.taskOperationMessageOpen = true;
        try {
            await this._messageModalService.alert(
                'A task operation is in progress. Please wait until it completes before closing this window.',
                'Task Operation In Progress',
            );
        } finally {
            this.taskOperationMessageOpen = false;
        }

        return Promise.reject();
    };

    getHeight(task): number {
        if (task.isReady) {
            return this.isDeliverable(task) ? this.heights.xl : this.heights.lg;
        } else if (this.isDeliverable(task)) {
            return this.heights.md;
        } else {
            return this.heights.sm;
        }
    }

    isDeliverable(task): boolean {
        return task.deliverableName || task.dueDate;
    }

    closeModal(): void {
        this._bsModalRef.hide();
    }

    getPlacement(index: number): string {
        if (
            index < 2 &&
            this.taskGroupsBySequence &&
            this._taskService.getMaxTasksWide(this.taskGroupsBySequence) > 2
        ) {
            return 'right';
        } else {
            return 'left';
        }
    }

    private closeIfRequested(): void {
        if (this.closeWhenIdle && this.pendingTaskOperations === 0) {
            this.closeWhenIdle = false;
            this.closeModal();
        }
    }
}
